import { Entypo, FontAwesome5, Ionicons, MaterialIcons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import * as WebBrowser from "expo-web-browser";
import { Locale } from "locale-enum";
import {
  Box,
  Button,
  Center,
  Circle,
  Divider,
  FormControl,
  Heading,
  HStack,
  Icon,
  Image,
  Pressable,
  Progress,
  ScrollView,
  Select,
  Text,
  useColorModeValue,
  useTheme,
  View,
  VStack,
} from "native-base";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as URI from "uri-js";

import { ASYNC_STORAGE__LANGUAGE_KEY } from "../../../i18n";
import { USER_LOGOUT_ACTION } from "../../../store";
import BlogLinksDropdown from "../components/BlogLinksDropdown";
import CustomBrandingMobileScreenHeader from "../components/CustomBrandingMobileScreenHeader";
import MenstrualCycleDiagram from "../components/MenstrualCycleDiagram";
import ScreenContainer from "../components/ScreenContainer";
import { BUILD_NUMBER, commonStyles, Images, isDesktopScreen, Routes, width as deviceWidth } from "../constants";
import { Scale, VerticalScale, width } from "../constants/helperFunction";
import { calculateDailyTotalForMacro } from "../helpers/coachHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { apiCallErrorHandler, isMobilePlatform } from "../helpers/generalHelpers";
import { logout } from "../helpers/logout";
import { ContactSupportLink, StripeCustomerPortalLink } from "../helpers/supportHelpers";
import {
  doesTheUserHaveMenstrualPlanSet,
  getMenstrualCyclePropsFromUser,
  hasUserCompletedSimpleOnboarding,
  hasUserCompletedTheirDiyNutritionPlanningFormCompletely,
  isSimpleOrg,
  isUserDiy,
  isUserFromSimpleOrganization,
  isUserInPaymentRequiredOrganisation,
  organisationIsDiyBillingForUsers,
} from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import logger from "../services/logger";
import { onboardingSlice } from "../slices/onboardingSlice";
import { simpleOnboardingSlice } from "../slices/simpleOnboardingSlice";
import { clientModeSelector, userSelector, userSlice } from "../slices/userSlice";
import styles from "./MyProfileScreenStyles";

const {
  useUsersUserDestroyMutation,
  useCheckoutSubscriptionVerificationCreateMutation,
  useUsersUserProfileUpdateMutation,
  useFoodIngredientToAvoidListQuery,
} = backendApi;

type SupportedLanguage = Locale.en | Locale.nl;
const LANGUAGE_OPTIONS: SupportedLanguage[] = [Locale.en, Locale.nl];

type Props = NativeStackScreenProps<RootStackParamList, Routes.MyProfileScreen>;
const MyProfileScreen = ({ navigation }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const isDesktop = isDesktopScreen();
  const isWeb = !isMobilePlatform();

  const user = useSelector(userSelector);
  const clientMode = useSelector(clientModeSelector);

  const theme = useTheme();

  const [deleteUserOnBackend] = useUsersUserDestroyMutation();
  const [updateUserProfile, { isLoading: isUpdatingUserProfile }] = useUsersUserProfileUpdateMutation();
  const { data: ingredientToAvoidData } = useFoodIngredientToAvoidListQuery({});

  // NOTE: This feature was here for app store compliance.
  // Since we are not updating our app store app this is here only for reference.
  const deleteAccountButtonPress = async (): Promise<void> => {
    if (!user) {
      throw new Error("User is not defined");
    }

    const proceedToNextStep = window.confirm(t("delete_account.warning"));

    if (!proceedToNextStep) {
      return;
    }

    const confirmKeyword = t("delete_account.confirm_keyword");
    const returnValue = prompt(t("delete_account.input_confirmation", { confirmKeyword }));

    if (returnValue === confirmKeyword) {
      await deleteUserOnBackend({
        id: user.id,
      });

      logout(dispatch);
    } else {
      alert(t("delete_account.abandon", { confirmKeyword }));
    }
  };

  const swapMode = (): void => {
    dispatch(userSlice.actions.toggleClientMode());
  };

  const formatLanguage = (language: SupportedLanguage): string => t(`general.locales.${language}`);

  const switchLanguage = async (language: Locale): Promise<void> => {
    await i18n.changeLanguage(language);

    await AsyncStorage.setItem(ASYNC_STORAGE__LANGUAGE_KEY, language);
    dispatch(userSlice.actions.storeLocale(language));
  };

  const shouldShowStripeCustomerPortalLink =
    (!clientMode && user && isWeb) || (user && organisationIsDiyBillingForUsers(user));

  // Format food intolerances for display
  const formatIntolerances = (): string => {
    const intolerances = [];

    if (user?.intake?.food_intolerances_gluten) {
      intolerances.push(t("general.intolerances_and_allergies.GLUTEN"));
    }
    if (user?.intake?.food_intolerances_lactose) {
      intolerances.push(t("general.intolerances_and_allergies.LACTOSE"));
    }
    if (user?.intake?.food_intolerances_nut) {
      intolerances.push(t("general.intolerances_and_allergies.NUTS"));
    }
    if (user?.intake?.food_intolerances_crustaceans_shellfish) {
      intolerances.push(t("general.intolerances_and_allergies.CRUSTACEANS_AND_SHELLFISH"));
    }

    if (intolerances.length === 0) {
      return t("onboarding.onboarding_9_intolerance_screen.no_intolerance");
    }

    return intolerances.join(", ");
  };

  // Format disliked ingredients for display
  const formatDislikedIngredients = (): string => {
    if (!user?.ingredients_to_avoid || user.ingredients_to_avoid.length === 0) {
      return t("general.none");
    }

    // If ingredient data is not loaded yet, show the IDs temporarily
    if (!ingredientToAvoidData?.results) {
      return user.ingredients_to_avoid.join(", ");
    }

    // Map ingredient IDs to names
    const ingredientNames = user.ingredients_to_avoid
      .map((ingredientId) => {
        const ingredient = ingredientToAvoidData.results.find((item) => item.id === ingredientId);
        return ingredient?.name || ingredientId; // Fallback to ID if name not found
      })
      .filter(Boolean);

    return ingredientNames.length > 0 ? ingredientNames.join(", ") : t("general.none");
  };

  // Get dietary preference display name
  const getDietaryPreference = (): string => {
    // For SIMPLE users, use their simple_diet preference
    if (isSimpleOrganization) {
      if (user?.intake?.simple_diet === null) {
        // User selected "I eat everything"
        return t("simple_onboarding.diet.none");
      }
      if (user?.intake?.simple_diet) {
        // User selected a specific diet (e.g., PESCATARIAN, VEGETARIAN, etc.)
        return t(`general.diets.${user.intake.simple_diet}`);
      }
    }

    // For non-SIMPLE users, use the regular diet field
    if (!user?.intake?.diet) {
      return t("general.diets.OMNIVORE");
    }
    return t(`general.diets.${user.intake.diet}`);
  };

  const isOnMenopauseDiet = (): boolean => !!user?.intake?.is_on_menopause_diet;

  // Check if user is in a SIMPLE organization
  const isSimpleOrganization = user ? isSimpleOrg(user) : false;

  // Format motivation for display
  const getMotivation = (): string => {
    if (!user?.intake?.onboarding_motivation) return "-";
    return t(`general.onboarding_motivation.${user.intake.onboarding_motivation}`);
  };

  // Format cooking styles for display
  const formatCookingStyles = (): string => {
    if (!user?.intake?.onboarding_cooking_styles || user.intake.onboarding_cooking_styles.length === 0) {
      return t("general.none");
    }
    return user.intake.onboarding_cooking_styles.map((style) => t(`general.cooking_styles.${style}`)).join(", ");
  };

  // Format preferred meal times
  const formatPreferredMealTimes = (): string => {
    if (!user?.intake?.preferred_meal_times || user.intake.preferred_meal_times.length === 0) {
      return t("general.none");
    }
    return user.intake.preferred_meal_times.map((time) => t(`general.meal_times.${time}`)).join(", ");
  };

  // Format simple allergens
  const formatSimpleAllergens = (): string => {
    if (!user?.intake?.simple_allergens || user.intake.simple_allergens.length === 0) {
      return t("general.none");
    }
    return user.intake.simple_allergens.map((allergen) => t(`general.simple_allergens.${allergen}`)).join(", ");
  };

  // Get goal display name
  const getGoal = (): string | undefined => {
    if (user?.intake?.main_goal) {
      return t(`general.goals.${user.intake.main_goal}`);
    }
    return undefined;
  };

  // Helper function to format height according to user preferences
  const formatHeight = (): string => {
    if (!user?.intake?.height_in_cm) return "-";

    // Check if user prefers imperial units for height
    const usesImperial = user.intake.uses_imperial_measurements_for_height;

    if (usesImperial) {
      // Convert cm to feet and inches
      const heightInInches = user.intake.height_in_cm / 2.54;
      const feet = Math.floor(heightInInches / 12);
      const inches = Math.round(heightInInches % 12);

      // Handle the case where inches is 12 (should roll over to next foot)
      if (inches === 12) {
        return `${feet + 1}' 0"`;
      }

      return `${feet}' ${inches}"`;
    }
    // Display in centimeters
    return `${user.intake.height_in_cm} ${t("onboarding.onboarding_5_physical_stats_screen.centimeters")}`;
  };

  // Helper function to format weight according to user preferences
  const formatWeight = (weightInKg: number | undefined | null): string => {
    if (!weightInKg) return "-";

    const weightUnit = user?.intake?.personal_weight_unit || "KILOGRAMS";

    if (weightUnit === "POUNDS") {
      const pounds = Math.round(weightInKg * 2.20462);
      return `${pounds} ${t("general.pounds")}`;
    }
    if (weightUnit === "STONES") {
      const totalPounds = weightInKg * 2.20462;
      const stones = Math.floor(totalPounds / 14);
      const pounds = Math.round(totalPounds % 14);
      return `${stones} st ${pounds} ${t("general.pounds")}`;
    }
    return `${weightInKg} ${t("onboarding.onboarding_5_physical_stats_screen.kilograms")}`;
  };

  const handleRecipeUnitChange = async (useImperial: boolean): Promise<void> => {
    if (!user || !user.intake) return;

    try {
      const userProfileRequest = {
        ...user.intake,
        uses_imperial_measurements_for_recipes: useImperial,
      };

      await updateUserProfile({
        id: user.intake.id,
        userProfileRequest,
      })
        .unwrap()
        .then(() => {
          alert(t("my_profile.recipe_units_updated_successfully"));
        })
        .catch(apiCallErrorHandler());
    } catch (error) {
      logger.error("Failed to update recipe units preference", error);
    }
  };

  const menstrualCycleProps = user ? getMenstrualCyclePropsFromUser(user) : undefined;

  async function handlePeriodStartedEarly(): Promise<void> {
    if (!user || !user.intake) return;

    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0]; // Format as YYYY-MM-DD

    try {
      const userProfileRequest = {
        ...user.intake,
        last_menstrual_period_start_date: formattedDate,
      };

      await updateUserProfile({
        id: user.intake.id,
        userProfileRequest,
      })
        .unwrap()
        .then(() => {
          alert(t("my_profile.menstrual_cycle.period_start_date_updated"));
        })
        .catch(apiCallErrorHandler());
    } catch (error) {
      logger.error("Failed to update menstrual period start date", error);
    }
  }

  const UserProfile = (): JSX.Element => (
    <ScrollView showsVerticalScrollIndicator={false}>
      <VStack space={6} px={4} py={6}>
        {/* Header Section */}
        <VStack space={5} alignItems="center">
          <Circle size={12} bg="function.10">
            <Icon as={MaterialIcons} name="person" size="lg" color="primary.50" />
          </Circle>
          <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mb={4}>
            {t("my_profile.profile_heading")}
          </Heading>
        </VStack>

        <Divider bg="function.40" />

        {/* Nutrition Macros Section - Hidden for SIMPLE organizations */}
        {!isSimpleOrganization && user?.intake ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("my_profile.macros_heading")}
            </Heading>

            <HStack space={4} justifyContent="space-between">
              {/* Calories */}
              <VStack alignItems="center" flex={1} space={2}>
                <Circle size={9} bg="function.10">
                  <Icon as={MaterialIcons} name="local-fire-department" size="sm" color="primary.400" />
                </Circle>
                <Text fontSize="sm" fontWeight="bold" color="function.50" fontFamily={theme.fonts.body}>
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "kcal",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications ? " kcl" : ""}
                </Text>
                <Text fontSize="xs" color="function.50" fontFamily={theme.fonts.body} textAlign="center">
                  {t("general.calories")}
                </Text>
              </VStack>

              {/* Protein */}
              <VStack alignItems="center" flex={1} space={2}>
                <Circle size={9} bg="function.10">
                  <Icon as={MaterialIcons} name="fitness-center" size="sm" color="primary.400" />
                </Circle>
                <Text fontSize="sm" fontWeight="bold" color="function.50" fontFamily={theme.fonts.body}>
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "protein",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications ? " g" : ""}
                </Text>
                <Text fontSize="xs" color="function.50" fontFamily={theme.fonts.body} textAlign="center">
                  {t("general.protein_short")}
                </Text>
              </VStack>

              {/* Carbs */}
              <VStack alignItems="center" flex={1} space={2}>
                <Circle size={9} bg="function.10">
                  <Icon as={MaterialIcons} name="grass" size="sm" color="primary.400" />
                </Circle>
                <Text fontSize="sm" fontWeight="bold" color="function.50" fontFamily={theme.fonts.body}>
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "carbohydrates",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications ? " g" : ""}
                </Text>
                <Text
                  fontSize="xs"
                  color="function.50"
                  fontFamily={theme.fonts.body}
                  textAlign="center"
                  numberOfLines={2}
                >
                  {t("general.carbohydrates_short")}
                </Text>
              </VStack>

              {/* Fat */}
              <VStack alignItems="center" flex={1} space={2}>
                <Circle size={9} bg="function.10">
                  <Icon as={MaterialIcons} name="opacity" size="sm" color="primary.400" />
                </Circle>
                <Text fontSize="sm" fontWeight="bold" color="function.50" fontFamily={theme.fonts.body}>
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "fat",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications ? " g" : ""}
                </Text>
                <Text fontSize="xs" color="function.50" fontFamily={theme.fonts.body} textAlign="center">
                  {t("general.fat")}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        ) : null}

        {!isSimpleOrganization && user?.intake ? <Divider bg="function.40" /> : null}

        {/* Profile Information Section - Hidden for SIMPLE organizations */}
        {!isSimpleOrganization ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("my_profile.profile_heading")}
            </Heading>

            <VStack space={6}>
              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("general.email_address")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {user?.email}
                </Text>
              </HStack>

              {getGoal() ? (
                <HStack justifyContent="space-between" alignItems="center" space={4}>
                  <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                    {t("coach_mode_create_new_client.goal_title_text")}
                  </Text>
                  <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                    {getGoal()}
                  </Text>
                </HStack>
              ) : null}

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("coach_mode_create_new_client.gender_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {user?.intake?.gender ? t(`general.gender.${user.intake.gender}`) : "-"}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("coach_mode_create_new_client.age_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {user?.intake?.age || "-"}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("coach_mode_create_new_client.height_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {formatHeight()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("coach_mode_create_new_client.weight_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {formatWeight(user?.intake?.weight)}
                </Text>
              </HStack>

              {user?.intake?.goal_weight_in_kg ? (
                <HStack justifyContent="space-between" alignItems="center" space={4}>
                  <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                    {t("onboarding.onboarding_5_physical_stats_screen.goal_weight_title")}
                  </Text>
                  <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                    {formatWeight(user?.intake?.goal_weight_in_kg)}
                  </Text>
                </HStack>
              ) : null}
            </VStack>
          </VStack>
        ) : null}

        {!isSimpleOrganization ? <Divider bg="function.40" /> : null}

        {/* Dietary Preferences Section - Hidden for SIMPLE organizations */}
        {!isSimpleOrganization ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("coach_mode_create_new_client.diet_title_text")}
            </Heading>

            <VStack space={6}>
              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("coach_mode_create_new_client.diet_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {getDietaryPreference()}
                </Text>
              </HStack>

              {isOnMenopauseDiet() ? (
                <HStack justifyContent="space-between" alignItems="center" space={4}>
                  <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                    {t("onboarding.onboarding_7_nutrition_plan_screen.menopause")}
                  </Text>
                  <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                    {t("onboarding.onboarding_menstrual_cycle_spacing_screen.yes")}
                  </Text>
                </HStack>
              ) : null}

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body} flex={1}>
                  {t("coach_mode_create_new_client.intolerances_and_allergies_title_text")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {formatIntolerances()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body} flex={1}>
                  {t("onboarding.onboarding_10_ingredient_dislikes_screen.title")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {formatDislikedIngredients()}
                </Text>
              </HStack>
            </VStack>
          </VStack>
        ) : null}

        {/* Recipe Preferences Section - Only show for SIMPLE organizations */}
        {isSimpleOrganization && user?.intake ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("my_profile.recipe_preferences_heading")}
            </Heading>

            <VStack space={6}>
              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("my_profile.motivation_label")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {getMotivation()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body} flex={1}>
                  {t("my_profile.cooking_styles_label")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right" flex={1}>
                  {formatCookingStyles()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body} flex={1}>
                  {t("my_profile.preferred_meal_times_label")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right" flex={1}>
                  {formatPreferredMealTimes()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("my_profile.dietary_preference_label")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {getDietaryPreference()}
                </Text>
              </HStack>

              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("my_profile.allergens_label")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {formatSimpleAllergens()}
                </Text>
              </HStack>
            </VStack>
          </VStack>
        ) : null}

        {isSimpleOrganization && user?.intake ? <Divider bg="function.40" /> : null}

        {/* Menstrual Cycle Section */}
        {user && doesTheUserHaveMenstrualPlanSet(user) ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("my_profile.menstrual_cycle.heading")}
            </Heading>

            {user && menstrualCycleProps !== undefined ? (
              <Center py={4}>
                <MenstrualCycleDiagram {...menstrualCycleProps} />
              </Center>
            ) : null}

            <VStack space={4}>
              {user?.intake?.last_menstrual_period_start_date ? (
                <HStack justifyContent="space-between" py={2}>
                  <Text fontWeight="medium" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                    {t("my_profile.menstrual_cycle.last_period_start")}
                  </Text>
                  <Text color="function.40" fontSize="sm" fontFamily={theme.fonts.body}>
                    {new Date(user.intake.last_menstrual_period_start_date).toLocaleDateString(
                      i18n.language.startsWith("nl") ? "nl-NL" : "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    )}
                  </Text>
                </HStack>
              ) : null}

              {user?.intake?.menstrual_cycle_length ? (
                <HStack justifyContent="space-between" py={2}>
                  <Text fontWeight="medium" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                    {t("my_profile.menstrual_cycle.cycle_length")}
                  </Text>
                  <Text color="function.40" fontSize="sm" fontFamily={theme.fonts.body}>
                    {user.intake.menstrual_cycle_length}{" "}
                    {t("onboarding.onboarding_menstrual_cycle_spacing_screen.days").toLowerCase()}
                  </Text>
                </HStack>
              ) : null}

              <Center pt={2}>
                <Button
                  bg="function.50"
                  color="function.10"
                  size="md"
                  leftIcon={<Icon as={MaterialIcons} name="event" size="sm" />}
                  onPress={() => {
                    handlePeriodStartedEarly().catch((err) => {
                      logger.error("Failed to update menstrual period start date", err);
                      alert(t("general.generic_error_message"));
                      throw err;
                    });
                  }}
                  testID="period-started-early-button"
                  borderRadius={theme.borderRadii.button.default}
                  px={6}
                  py={3}
                  _pressed={{ bg: "function.40" }}
                >
                  {t("my_profile.menstrual_cycle.period_started_early")}
                </Button>
              </Center>
            </VStack>
          </VStack>
        ) : null}

        {user && doesTheUserHaveMenstrualPlanSet(user) ? <Divider bg="function.40" /> : null}

        {/* Units & Preferences Section - Hidden for SIMPLE organizations */}
        {!isSimpleOrganization ? (
          <VStack space={8}>
            <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={0} mb={2}>
              {t("my_profile.units_heading")}
            </Heading>

            <VStack space={6}>
              <VStack space={3}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("my_profile.recipe_units")}
                </Text>
                <HStack space={6}>
                  <Pressable
                    onPress={() => {
                      if (!isUpdatingUserProfile) {
                        handleRecipeUnitChange(false).catch((error) => {
                          logger.error("Failed to update recipe units", error);
                        });
                      }
                    }}
                    isDisabled={isUpdatingUserProfile}
                  >
                    <HStack space={1} alignItems="center">
                      <Box
                        size={4}
                        borderRadius="full"
                        borderWidth={1}
                        borderColor={
                          !user?.intake?.uses_imperial_measurements_for_recipes ? "primary.400" : "function.40"
                        }
                        bg={!user?.intake?.uses_imperial_measurements_for_recipes ? "primary.400" : "transparent"}
                        alignItems="center"
                        justifyContent="center"
                      >
                        {!user?.intake?.uses_imperial_measurements_for_recipes && (
                          <Box size={2} borderRadius="full" bg="white" />
                        )}
                      </Box>
                      <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                        {t("my_profile.metric_units")}
                      </Text>
                    </HStack>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      if (!isUpdatingUserProfile) {
                        handleRecipeUnitChange(true).catch((error) => {
                          logger.error("Failed to update recipe units", error);
                        });
                      }
                    }}
                    isDisabled={isUpdatingUserProfile}
                  >
                    <HStack space={1} alignItems="center">
                      <Box
                        size={4}
                        borderRadius="full"
                        borderWidth={1}
                        borderColor={
                          !!user?.intake?.uses_imperial_measurements_for_recipes ? "primary.400" : "function.40"
                        }
                        bg={!!user?.intake?.uses_imperial_measurements_for_recipes ? "primary.400" : "transparent"}
                        alignItems="center"
                        justifyContent="center"
                      >
                        {!!user?.intake?.uses_imperial_measurements_for_recipes && (
                          <Box size={2} borderRadius="full" bg="white" />
                        )}
                      </Box>
                      <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                        {t("my_profile.imperial_units")}
                      </Text>
                    </HStack>
                  </Pressable>
                </HStack>
                {isUpdatingUserProfile ? (
                  <Progress size="sm" colorScheme="primary" bg="function.20" borderRadius={theme.borderRadii.xs} />
                ) : null}
              </VStack>
            </VStack>
          </VStack>
        ) : null}

        {!isSimpleOrganization ? <Divider bg="function.40" /> : null}

        {/* System Section */}
        <VStack space={8}>
          <Heading size="lg" color="function.50" fontFamily={theme.fonts.heading} mt={2} mb={2}>
            {t("my_profile.system_heading")}
          </Heading>

          <VStack space={6}>
            {/* Email Address - Show for SIMPLE organizations in system section */}
            {isSimpleOrganization ? (
              <HStack justifyContent="space-between" alignItems="center" space={4}>
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("general.email_address")}
                </Text>
                <Text color="function.50" fontSize="sm" fontFamily={theme.fonts.body} textAlign="right">
                  {user?.email}
                </Text>
              </HStack>
            ) : null}

            {/* Language Selector */}
            {!isFeatureFlagActive(user, FeatureFlag.FF_ENGLISH_LANGUAGE_ONLY) && (
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                  {t("my_profile.language_chooser.section_title")}
                </Text>
                <Box width="142px">
                  <Select
                    bg="transparent"
                    borderColor="function.20"
                    borderRadius={theme.borderRadii.input.default}
                    defaultValue={i18n.language}
                    accessibilityLabel={t("my_profile.language_chooser.language_select_placeholder")}
                    onValueChange={(language) => {
                      switchLanguage(language as Locale).catch((error) => {
                        logger.error("Failed to switch language", error);
                      });
                    }}
                    testID="language-selector"
                    fontSize="sm"
                    color="function.40"
                    _selectedItem={{
                      bg: "primary.100",
                      endIcon: <Icon as={MaterialIcons} name="check" size="sm" color="primary.600" />,
                    }}
                  >
                    {LANGUAGE_OPTIONS.map((language) => (
                      <Select.Item key={language} label={formatLanguage(language)} value={language} />
                    ))}
                  </Select>
                </Box>
              </HStack>
            )}

            {/* Help / Contact Us */}
            <HStack justifyContent="space-between" alignItems="center" space={4}>
              <Text fontWeight="bold" color="function.50" fontSize="sm" fontFamily={theme.fonts.body}>
                {t("my_profile.get_help_button_text")}
              </Text>
              <ContactSupportLink user={user} size="sm" width="auto" mt={0} shadow={2} borderWidth={1} />
            </HStack>
          </VStack>
        </VStack>

        {/* Action Buttons Section */}
        <Box mt={10} width="100%">
          <VStack space={8} width="100%" alignItems="center">
            <VStack space={4} width="100%" alignItems="center">
              {/* Redo Nutrition Planning Button */}
              {user &&
              isUserDiy(user) &&
              !isSimpleOrg(user) &&
              clientMode &&
              hasUserCompletedTheirDiyNutritionPlanningFormCompletely(user) ? (
                <Button
                  onPress={() => {
                    if (!user) {
                      throw new Error("User is not defined");
                    }
                    if (!user.intake) {
                      throw new Error("User intake is not defined");
                    }
                    dispatch(onboardingSlice.actions.setUserProfileData(user.intake));
                    // NOTE: There is an error with the linter but this is valid typescript
                    // eslint-disable-next-line react/prop-types
                    navigation.push(Routes.DIY1GoalScreen);
                  }}
                  testID={"redoNutritionPlanning-Button"}
                  nativeID={"redoNutritionPlanningButton"}
                  bg="primary.50"
                  size="md"
                  borderRadius={theme.borderRadii.button.default}
                  _text={{ color: "white", fontSize: "sm", fontFamily: theme.fonts.body }}
                  _pressed={{ bg: "primary.50" }}
                  width="auto"
                  px={6}
                  shadow={2}
                >
                  {t("my_profile.redo_nutrition_planning_button_text")}
                </Button>
              ) : null}

              {/* Redo Simple Preferences Button */}
              {user && isUserFromSimpleOrganization(user) && clientMode && hasUserCompletedSimpleOnboarding(user) ? (
                <Button
                  onPress={() => {
                    // Pre-populate the simple onboarding form with existing user data
                    if (user?.intake) {
                      dispatch(simpleOnboardingSlice.actions.populateFromUserProfile(user.intake));
                    }
                    // Navigate to the first simple onboarding screen
                    // NOTE: There is an error with the linter but this is valid typescript
                    // eslint-disable-next-line react/prop-types
                    navigation.push(Routes.SimpleOnboarding_1_Motivation);
                  }}
                  testID={"redoSimplePreferences-Button"}
                  nativeID={"redoSimplePreferencesButton"}
                  bg="primary.50"
                  size="md"
                  borderRadius={theme.borderRadii.button.default}
                  _text={{ color: "white", fontSize: "sm", fontFamily: theme.fonts.body }}
                  _pressed={{ bg: "primary.50" }}
                  width="auto"
                  px={6}
                  shadow={2}
                >
                  {t("my_profile.redo_simple_preferences_button_text")}
                </Button>
              ) : null}

              {/* Subscription Management */}
              {shouldShowStripeCustomerPortalLink ? (
                <StripeCustomerPortalLink
                  isDesktop={isDesktop}
                  size="md"
                  width="auto"
                  iconType="material"
                  bg="primary.50"
                  _pressed={{ bg: "primary.50" }}
                  px={6}
                  shadow={2}
                />
              ) : null}

              {/* Coach Mode Toggle */}
              {user?.is_coach ? (
                <Button
                  onPress={swapMode}
                  testID={"swapMode-Button"}
                  nativeID={"swapModeButton"}
                  bg="primary.50"
                  size="md"
                  borderRadius={theme.borderRadii.button.default}
                  _text={{ color: "white", fontSize: "sm", fontFamily: theme.fonts.body }}
                  _pressed={{ bg: "primary.50" }}
                  width="auto"
                  px={6}
                  shadow={2}
                >
                  {t("coach_mode_settings.swap_mode_button_text", {
                    modeName: t(
                      `coach_mode_settings.${clientMode ? "coach_mode_label_text" : "client_mode_label_text"}`
                    ),
                  })}
                </Button>
              ) : null}
            </VStack>

            {/* Logout Button */}
            <Button
              onPress={() => logout(dispatch)}
              testID="logout-button"
              nativeID="logoutButton"
              variant="outline"
              borderColor="function.20"
              size="sm"
              borderRadius={theme.borderRadii.button.default}
              _text={{ color: "function.20", fontSize: "sm", fontFamily: theme.fonts.body }}
              _pressed={{ bg: "function.10", borderColor: "function.30" }}
              width="auto"
              px={4}
            >
              {t("general.logout")}
            </Button>
          </VStack>
        </Box>
      </VStack>
    </ScrollView>
  );

  return (
    <ScreenContainer testID="my-profile-screen" safeAreaMode="top-horizontal">
      {!isDesktop && <CustomBrandingMobileScreenHeader />}
      <UserProfile />
    </ScreenContainer>
  );
};

export default MyProfileScreen;
